import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Autocomplete, Box, Paper, Stack, TextField, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import { DeclineReasonOption } from 'modules/creditSubmission/types';
import { SectionTitle } from './SectionTitle';
import { CreditSubmission } from '@trnsact/trnsact-shared-types';
import { useCreditSubmission } from 'modules/creditSubmission/hooks/useCreditSubmission';
import { useNotifications } from 'modules/notification';
import { useDeclineReasons } from 'modules/creditSubmission/hooks/useDeclineReasons';

interface Props {
  parsedReasons: string[];
  creditSubmission: CreditSubmission & {
    creditSubmissionDeclineReasonId?: string | null;
    masterCreditSubmissionId?: string | null;
  };
}

export const DeclineReasonsCard = ({ parsedReasons, creditSubmission }: Props) => {
  const lenderProfiles = useSelector((state: any) => state.lp);
  const { showNotification } = useNotifications();
  const { handleUpdateCreditSubmission } = useCreditSubmission();
  const lastSavedRef = useRef<string | null>(creditSubmission.creditSubmissionDeclineReasonId ?? null);
  const [selectedReason, setSelectedReason] = useState<DeclineReasonOption | null | undefined>(undefined);

  const lenderAccountId = lenderProfiles.find((lp: any) => lp.lenderProfileId === creditSubmission.lenderProfileId)
    ?.lenderAccount.id;

  const { options: declineReasonOptions, selectedOption } = useDeclineReasons({
    accountId: lenderAccountId,
    creditSubmissionDeclineReasonId: creditSubmission.creditSubmissionDeclineReasonId,
  });

  const currentValue = selectedReason !== undefined ? selectedReason : selectedOption;

  const handleChange = (_: unknown, option: DeclineReasonOption | null) => {
    setSelectedReason(option);
  };

  const handleBlur = async () => {
    const newValue = currentValue?.value || null;
    if (newValue === lastSavedRef.current) return;

    try {
      await handleUpdateCreditSubmission(creditSubmission.creditSubId!, {
        creditSubmissionDeclineReasonId: newValue,
      });
      lastSavedRef.current = newValue;
      showNotification('Decline reason saved');
    } catch {
      showNotification('Something went wrong while saving', { type: 'error' });
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        border: '1px solid',
        borderColor: grey[300],
        backgroundColor: grey[50],
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      <SectionTitle>Decline Reasons</SectionTitle>

      <Box sx={{ maxWidth: 400 }}>
        <Autocomplete
          fullWidth
          value={currentValue}
          options={declineReasonOptions.sort((a, b) => -b.category.localeCompare(a.category))}
          groupBy={option => option.category}
          getOptionLabel={option => option.label}
          isOptionEqualToValue={(option, value) => option.value === value?.value}
          onChange={handleChange}
          onBlur={handleBlur}
          renderInput={params => <TextField {...params} label="Select Reason" />}
        />
      </Box>

      {parsedReasons.length > 0 ? (
        <Stack gap={1} sx={{ mt: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Parsed Decline Reasons
          </Typography>
          {parsedReasons.map((reason, idx) => (
            <Box
              key={idx}
              sx={{
                py: 1,
                borderBottom: idx < parsedReasons.length - 1 ? '1px solid' : 'none',
                borderColor: 'rgba(0,0,0,0.08)',
              }}
            >
              <Typography variant="body1">{reason}</Typography>
            </Box>
          ))}
        </Stack>
      ) : !currentValue ? (
        <Typography variant="subtitle2" color="text.secondary" component="p">
          No decline reasons provided.
        </Typography>
      ) : null}
    </Paper>
  );
};
